// Drawer Component
// This file defines a slide-in drawer component with tabbed content and a close button.

import { styled } from "@stitches/react";
import { Tabs, TabPane, Button } from "@/vendor/Forge/dist";
import CloseIcon from "../Icons/close";

/**
 * Props for the Drawer component
 * @param open - Whether the drawer is open
 * @param handleDrawerToggle - Handler to open/close the drawer
 * @param children - Optional children (not used, content is from tabs)
 * @param tabs - Array of tab objects with label and component
 */
interface Props {
  open: boolean;
  handleDrawerToggle: () => void;
  children?: React.ReactNode;
  tabs: { tabLabel: string; component: React.ReactNode }[];
}

/**
 * DrawerContentContainer
 * Styled container for the drawer content, slides in from the right.
 */
const DrawerContentContainer = styled("div", {
  position: "fixed",
  top: 0,
  bottom: 0,
  right: 0,
  width: "100%",
  backgroundColor: "#fff",
  zIndex: 100,
  transform: "translateX(100%)",
  transition: "transform 0.3s ease-in-out",
  overflowY: "auto",

  "& .fe_c_tabs__labels": {
    backgroundColor: "#f8f8f8 !important",
    borderBottom: "1px solid #e0e0e0 !important",
    padding: "10px !important",
  },

  variants: {
    open: {
      true: {
        transform: "translateX(0)",
      },
    },
  },
});

/**
 * DrawerBackdrop
 * Styled backdrop overlay for the drawer, appears when open.
 */
const DrawerBackdrop = styled("div", {
  position: "fixed",
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "rgba(0, 0, 0, .5)",
  zIndex: 90,
  transition: "opacity 0.3s ease-in-out",
  opacity: 0,
  pointerEvents: "none",

  variants: {
    open: {
      true: {
        opacity: 0.8,
        pointerEvents: "auto",
      },
    },
  },
});

/**
 * CloseIconWrapper
 * Positions the close button in the top-right corner of the drawer.
 */
const CloseIconWrapper = styled("div", {
  position: "absolute",
  top: 8,
  right: 8,
});

/**
 * PaneComponentWrapper
 * Wrapper for the content of each tab pane.
 */
const PaneComponentWrapper = styled("div", {
  padding: "20px",
  height: "calc(100vh - 60px)",
  overflowY: "auto",
});

/**
 * Drawer Component
 * Renders a slide-in drawer with tabbed content and a close button.
 *
 * @param open - Whether the drawer is open
 * @param handleDrawerToggle - Handler to open/close the drawer
 * @param tabs - Array of tab objects with label and component
 */
const Drawer = ({ open, handleDrawerToggle, tabs }: Props) => {
  return (
    <>
      {/* Backdrop overlay, closes drawer on click */}
      <DrawerBackdrop
        data-testid="drawer-backdrop"
        open={open}
        onClick={handleDrawerToggle}
      />
      {/* Drawer content slides in from the right */}
      <DrawerContentContainer
        data-testid="drawer-content-container"
        open={open}
      >
        <Tabs>
          {tabs.map(({ tabLabel, component }) => (
            <TabPane padded={false} key={tabLabel} label={tabLabel}>
              <PaneComponentWrapper>{component}</PaneComponentWrapper>
            </TabPane>
          ))}
        </Tabs>
        {/* Close button in the top-right corner */}
        <CloseIconWrapper>
          <Button variant="tertiary" icon="close" onClick={handleDrawerToggle}>
            <CloseIcon />
          </Button>
        </CloseIconWrapper>
      </DrawerContentContainer>
    </>
  );
};

export default Drawer;
